import { captureException } from "@sentry/browser";
import React from "react";

interface IErrorInfo {
    componentStack: string;
}

interface IProps {
    children?: React.ReactNode;
    onError?: (error: Error, info: IErrorInfo) => void;
    getErrorMessage?: (error: Error, info: IErrorInfo) => React.ReactNode;
}

interface IState {
    details: null | {
        error: Error;
        info: IErrorInfo;
    };
}

// Note: This component must remain a class component because Error Boundaries require
// the componentDidCatch lifecycle method, which is not yet available as a React hook.
// See: https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
export class ErrorBoundary extends React.PureComponent<IProps, IState> {
    public state: IState = {
        details: null,
    };

    componentDidCatch(error: Error, info: IErrorInfo): void {
        this.setState({
            details: {
                error: error,
                info: info,
            },
        });
        captureException(error);
        if (this.props.onError) {
            this.props.onError(error, info);
        }
    }

    render(): React.ReactNode {
        if (this.state.details) {
            let details: React.ReactNode =
                "An error occurred while attempting to display this content.";
            if (this.props.getErrorMessage) {
                try {
                    details = this.props.getErrorMessage(
                        this.state.details.error,
                        this.state.details.info,
                    );
                } catch (e) {
                    console.error(e);
                }
            }
            return <div className="react-error-boundary">{details}</div>;
        }
        return this.props.children;
    }
}
